import React, { memo, useCallback, useMemo, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { Box, Button, Popover } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { NAVIGATION_CONTAINER_TYPES, NAVIGATION_ITEM_TYPE } from 'constants/navigation';
import usePopover from 'hooks/usePopover';
import { getIsNativePlatform, isTouchPrimary } from 'utils/platform';

import NavigationMenuGroupItems from './NavigationMenuGroupItems';
import {
  endIconWrapperStyles,
  getButtonStyles,
  getPopoverPaperStyles,
  popoverStyles,
} from './NavigationMenuItem.styles';
import { NavigationMenuItemTestIds } from './NavigationMenuItem.testIds';

const getGroupMenuSlopProps = ({ isVertical }) => ({
  paper: {
    sx: getPopoverPaperStyles({ isVertical }),
  },
});

const NavigationMenuItem = ({
  isVertical = false,
  isMeasurement = false,
  onCloseSidebar,
  ...item
}) => {
  const location = useLocation();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const searchInputRef = useRef(null);

  const onPopoverEntered = useCallback(() => {
    if (isTouchPrimary() || getIsNativePlatform()) {
      return;
    }

    searchInputRef.current?.focus();
  }, []);

  const popoverTransitionProps = useMemo(
    () => ({ onEntered: onPopoverEntered }),
    [onPopoverEntered],
  );

  const { id, title, url, type } = item;
  const isCurrentUrl = !!matchPath(location.pathname, { path: url, end: false });
  const isGroup = type === NAVIGATION_ITEM_TYPE.GROUP;
  const isContainer = NAVIGATION_CONTAINER_TYPES.includes(type);
  const isOpen = anchorEl?.dataset?.id === id;
  const isActive = isCurrentUrl || isOpen;

  const onSingleItemButtonClick = isVertical ? onCloseSidebar : undefined;

  return (
    <>
      <Button
        color="primary"
        component={!isContainer ? Link : undefined}
        data-id={id}
        data-is-active={isActive}
        data-testid={isMeasurement ? null : NavigationMenuItemTestIds.getTabId(title)}
        endIcon={
          isContainer ? (
            <Box component="span" sx={endIconWrapperStyles}>
              <ArrowCheckIcon direction={isOpen ? 'up' : 'down'} size={IconSize[16]} />
            </Box>
          ) : undefined
        }
        sx={getButtonStyles({ isVertical })}
        to={!isContainer ? url : undefined}
        onClick={isContainer ? onSetAnchorElement : onSingleItemButtonClick}
      >
        {title}
      </Button>
      {isContainer && (
        <Popover
          disableEnforceFocus
          TransitionProps={popoverTransitionProps}
          anchorEl={anchorEl}
          id="group-menu"
          open={isOpen}
          slotProps={getGroupMenuSlopProps({ isVertical })}
          sx={popoverStyles}
          onClose={onClearAnchorElement}
        >
          <NavigationMenuGroupItems
            isVertical={isVertical}
            withSearch={isGroup}
            {...item}
            searchRef={searchInputRef}
            onClose={onClearAnchorElement}
            onCloseSidebar={onCloseSidebar}
          />
        </Popover>
      )}
    </>
  );
};

NavigationMenuItem.propTypes = {
  isVertical: PropTypes.bool,
  isMeasurement: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default memo(NavigationMenuItem);
